import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import {
  FaCommentDots,
  FaTimes,
  FaArrowUp,
  FaStop,
  FaRedo,
  FaExclamationCircle,
} from 'react-icons/fa';

import { useAuth } from '../../context/AuthContext';
import useSiteChat from './useSiteChat';
import renderRichText from './richText';

/**
 * ChatWidget — the "Got questions?" assistant in the bottom-right of the public site.
 *
 * Lives once, in App.js, above the router outlet, so opening it and then following a
 * link the assistant gave you keeps the conversation on screen. That is the reason
 * links inside replies render as react-router <Link>s and not <a href>s: a full page
 * load would throw the visitor out of the conversation mid-answer.
 *
 * Every state the visitor can be in is drawn explicitly — idle, sending, waiting on a
 * reply, streaming, stopped, rate-limited, failed — because the states a chat widget
 * hides are exactly the ones that make it feel broken.
 */

/** Surfaces where a marketing assistant does not belong. */
const HIDDEN_PREFIXES = [
  '/developer',
  '/login',
  '/limelite/account',
  '/limelite/dashboard',
  '/limelite/deploy/account',
  '/limelite/deploy/dashboard',
  '/limelite/deploy/infrastructure',
  '/limelite/deploy/login',
  '/limelite/deploy/projects',
  '/limelite/deploy/forgot-password',
  '/limelite/deploy/reset-password',
  '/limelite/storefront/dashboard',
  '/limelite/storefront/provisioning',
];

const TEASER_KEY = 'rts-site-chat-teaser-dismissed';
const TEASER_DELAY_MS = 7000;

/* ── small presentational pieces ─────────────────────────────────────────── */

const TypingDots = () => (
  <span className="inline-flex items-center gap-1 py-1" aria-hidden="true">
    {[0, 1, 2].map((i) => (
      <span
        key={i}
        className="h-1.5 w-1.5 rounded-full bg-moss-light/70"
        style={{ animation: `rts-chat-dot 1.2s ${i * 0.16}s infinite ease-in-out` }}
      />
    ))}
  </span>
);

const Avatar = () => (
  <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full border border-moss-deep/60 bg-moss-night text-[11px] font-semibold tracking-wide text-moss-light">
    R
  </span>
);

function StatusLine({ status }) {
  const label =
    status === 'waiting' ? 'Thinking…' : status === 'streaming' ? 'Replying…' : 'Usually replies instantly';
  const live = status === 'waiting' || status === 'streaming';
  return (
    <span className="flex items-center gap-1.5 text-[11px] text-silver-dark">
      <span
        className={`h-1.5 w-1.5 rounded-full ${live ? 'bg-moss-light' : 'bg-moss'} ${
          live ? 'animate-pulse' : ''
        }`}
      />
      {label}
    </span>
  );
}

function Message({ message, linkPaths, linkLabels, onNavigate }) {
  const isUser = message.role === 'user';

  if (isUser) {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-moss-deep/50 px-3.5 py-2 text-sm leading-relaxed text-silver-light">
          <p className="whitespace-pre-wrap">{message.content}</p>
        </div>
      </div>
    );
  }

  const empty = !message.content.trim();

  return (
    <div className="flex items-start gap-2.5">
      <Avatar />
      <div className="min-w-0 max-w-[88%] space-y-1.5 rounded-2xl rounded-bl-sm border border-gray-800 bg-off-black px-3.5 py-2.5 text-sm leading-relaxed text-silver-medium">
        {empty ? (
          <TypingDots />
        ) : (
          <>
            {renderRichText(message.content, { allowedPaths: linkPaths, linkLabels, onNavigate })}
            {message.status === 'streaming' && (
              <span className="ml-0.5 inline-block h-3.5 w-[2px] translate-y-0.5 bg-moss-light/80 align-middle animate-pulse" />
            )}
          </>
        )}
        {message.status === 'stopped' && (
          <p className="pt-1 text-[11px] italic text-silver-dark">Stopped.</p>
        )}
      </div>
    </div>
  );
}

/* ── the widget ──────────────────────────────────────────────────────────── */

const ChatWidget = () => {
  const location = useLocation();
  const reduceMotion = useReducedMotion();
  const { consentGiven, isLoading: consentLoading } = useAuth();
  const { config, messages, status, error, send, retry, stop, reset } = useSiteChat();

  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [teaser, setTeaser] = useState(false);
  const [stick, setStick] = useState(true);
  const [navOpen, setNavOpen] = useState(false);

  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const launcherRef = useRef(null);

  const busy = status === 'waiting' || status === 'streaming';
  const hidden = HIDDEN_PREFIXES.some((p) => location.pathname.startsWith(p));
  const consentBannerUp = !consentLoading && consentGiven === null;

  /* The cookie banner is a full-width bar pinned to the bottom. Sitting on top of
     it looks like a bug, so the widget rides above it until it is dismissed. */
  const lift = consentBannerUp ? 'bottom-32 sm:bottom-28' : 'bottom-5 sm:bottom-6';

  /* Teaser: one gentle nudge per session, and never over the consent banner —
     two things demanding attention at once is one thing too many. */
  useEffect(() => {
    if (open || hidden || consentBannerUp || !config?.enabled) return undefined;
    if (messages.length > 0) return undefined;
    try {
      if (window.sessionStorage.getItem(TEASER_KEY)) return undefined;
    } catch {
      /* storage blocked — showing the teaser once is the harmless default */
    }
    const timer = setTimeout(() => setTeaser(true), TEASER_DELAY_MS);
    return () => clearTimeout(timer);
  }, [open, hidden, consentBannerUp, config, messages.length]);

  /* Navbar tells us when the mobile menu is open. We sit above it in z-order, so
     without this the bubble covers the lower menu rows and swallows their taps. */
  useEffect(() => {
    const onNav = (e) => {
      const open = Boolean(e.detail?.open);
      setNavOpen(open);
      if (open) setOpen(false);
    };
    window.addEventListener('rts:mobile-menu', onNav);
    return () => window.removeEventListener('rts:mobile-menu', onNav);
  }, []);

  const dismissTeaser = useCallback(() => {
    setTeaser(false);
    try {
      window.sessionStorage.setItem(TEASER_KEY, '1');
    } catch {
      /* nothing to persist */
    }
  }, []);

  const openPanel = useCallback(() => {
    dismissTeaser();
    setOpen(true);
    setStick(true);
  }, [dismissTeaser]);

  const closePanel = useCallback(() => {
    setOpen(false);
    launcherRef.current?.focus();
  }, []);

  // Esc closes — expected of anything that floats over the page.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closePanel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, closePanel]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  /* Follow the stream to the bottom, but stop the moment the visitor scrolls up —
     yanking someone back down while they are re-reading an answer is hostile. */
  useEffect(() => {
    if (!open || !stick) return;
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [open, stick, messages, status]);

  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setStick(el.scrollHeight - el.scrollTop - el.clientHeight < 48);
  }, []);

  const submit = useCallback(
    (text) => {
      const value = (text ?? draft).trim();
      if (!value || busy) return;
      send(value);
      setDraft('');
      setStick(true);
      if (inputRef.current) inputRef.current.style.height = 'auto';
    },
    [draft, busy, send]
  );

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const onDraftChange = (e) => {
    setDraft(e.target.value);
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  };

  // No key configured, or the config never loaded → render nothing at all rather
  // than a bubble that fails on the first message. navOpen keeps us out of the
  // way of the mobile menu.
  if (hidden || navOpen || !config?.enabled) return null;

  const maxLength = config.maxLength || 1200;
  const nearLimit = draft.length > maxLength - 150;
  const spring = reduceMotion ? { duration: 0 } : { type: 'spring', stiffness: 380, damping: 30 };

  return (
    <>
      {/* Keyframes the widget owns. Tailwind's config is shared across the site;
          a chat-only animation does not belong in it. */}
      <style>{`
        @keyframes rts-chat-dot {
          0%, 60%, 100% { opacity: .25; transform: translateY(0); }
          30%           { opacity: 1;   transform: translateY(-3px); }
        }
      `}</style>

      {/* ── Launcher + teaser ─────────────────────────────────────────────── */}
      {/* mb clears the iPhone home indicator, which otherwise sits over the bubble. */}
      <div
        className={`fixed right-4 sm:right-6 ${lift} z-[60] flex items-center gap-2.5 transition-all duration-500`}
        style={{ marginBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <AnimatePresence>
          {teaser && !open && (
            <motion.div
              key="teaser"
              initial={reduceMotion ? { opacity: 0 } : { opacity: 0, x: 12, scale: 0.96 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={reduceMotion ? { opacity: 0 } : { opacity: 0, x: 8, scale: 0.96 }}
              transition={spring}
              className="flex items-center gap-2 rounded-full border border-moss-deep/60 bg-moss-night/95 py-2 pl-4 pr-2 shadow-xl backdrop-blur-sm"
            >
              <button
                type="button"
                onClick={openPanel}
                className="whitespace-nowrap text-sm text-silver-light hover:text-moss-light transition-colors"
              >
                Got questions? Ask here.
              </button>
              <button
                type="button"
                onClick={dismissTeaser}
                aria-label="Dismiss"
                className="grid h-6 w-6 place-items-center rounded-full text-silver-dark hover:bg-black/40 hover:text-silver-light transition-colors"
              >
                <FaTimes className="text-[10px]" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        <motion.button
          ref={launcherRef}
          type="button"
          onClick={() => (open ? closePanel() : openPanel())}
          aria-label={open ? 'Close chat' : 'Open chat — ask a question'}
          aria-expanded={open}
          whileHover={reduceMotion ? undefined : { scale: 1.06 }}
          whileTap={reduceMotion ? undefined : { scale: 0.94 }}
          className="relative grid h-14 w-14 place-items-center rounded-full border border-moss-deep/60 bg-moss-night text-moss-light shadow-2xl shadow-black/50 transition-colors hover:border-moss hover:text-silver-light focus:outline-none focus-visible:ring-2 focus-visible:ring-moss focus-visible:ring-offset-2 focus-visible:ring-offset-black"
        >
          {/* A slow halo — enough motion to be noticed in peripheral vision,
              not enough to compete with the page. */}
          {!open && !reduceMotion && (
            <span className="absolute inset-0 rounded-full border border-moss/40 animate-ping opacity-40" />
          )}
          <AnimatePresence mode="wait" initial={false}>
            <motion.span
              key={open ? 'close' : 'chat'}
              initial={reduceMotion ? false : { opacity: 0, rotate: -45, scale: 0.6 }}
              animate={{ opacity: 1, rotate: 0, scale: 1 }}
              exit={reduceMotion ? { opacity: 0 } : { opacity: 0, rotate: 45, scale: 0.6 }}
              transition={{ duration: reduceMotion ? 0 : 0.18 }}
              className="relative"
            >
              {open ? <FaTimes className="text-lg" /> : <FaCommentDots className="text-xl" />}
            </motion.span>
          </AnimatePresence>
        </motion.button>
      </div>

      {/* ── Panel ─────────────────────────────────────────────────────────── */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="panel"
            role="dialog"
            aria-label="Chat with Riv, the Riviera Tech assistant"
            initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 16, scale: 0.98 }}
            transition={spring}
            style={{ transformOrigin: 'bottom right', marginBottom: 'env(safe-area-inset-bottom, 0px)' }}
            className={`fixed inset-x-3 top-20 z-[61] flex flex-col overflow-hidden rounded-2xl border border-gray-800 bg-black/95 shadow-2xl shadow-black/70 backdrop-blur-md sm:inset-x-auto sm:top-auto sm:right-6 sm:h-[min(620px,calc(100vh-11rem))] sm:w-[400px] ${
              consentBannerUp ? 'bottom-48 sm:bottom-44' : 'bottom-24 sm:bottom-24'
            }`}
          >
            {/* Header */}
            <header className="flex items-center gap-3 border-b border-gray-800 bg-moss-night/60 px-4 py-3">
              <Avatar />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-silver-light">Riv</p>
                <StatusLine status={status} />
              </div>
              {messages.length > 0 && (
                <button
                  type="button"
                  onClick={reset}
                  className="rounded px-2 py-1 text-[11px] text-silver-dark hover:text-silver-light transition-colors"
                >
                  Clear
                </button>
              )}
              <button
                type="button"
                onClick={closePanel}
                aria-label="Close chat"
                className="grid h-8 w-8 place-items-center rounded-full text-silver-dark hover:bg-off-black hover:text-silver-light transition-colors"
              >
                <FaTimes className="text-sm" />
              </button>
            </header>

            {/* Transcript */}
            <div
              ref={scrollRef}
              onScroll={onScroll}
              className="flex-1 space-y-3.5 overflow-y-auto px-4 py-4"
            >
              {/* Greeting is client-side furniture, not a stored turn — it should
                  never be sent back as conversation history. */}
              <div className="flex items-start gap-2.5">
                <Avatar />
                <div className="max-w-[88%] rounded-2xl rounded-bl-sm border border-gray-800 bg-off-black px-3.5 py-2.5 text-sm leading-relaxed text-silver-medium">
                  {config.greeting}
                </div>
              </div>

              {messages.length === 0 && config.suggestions?.length > 0 && (
                <div className="flex flex-wrap gap-2 pl-9 pt-0.5">
                  {config.suggestions.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => submit(s)}
                      className="rounded-full border border-gray-800 bg-off-black px-3 py-1.5 text-xs text-silver-medium transition-colors hover:border-moss-deep hover:text-moss-light"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}

              {messages.map((m) => (
                <Message
                  key={m.id}
                  message={m}
                  linkPaths={config.linkPaths}
                  linkLabels={config.linkLabels}
                  onNavigate={closePanel}
                />
              ))}

              {error && (
                <div
                  role="alert"
                  className="flex items-start gap-2.5 rounded-xl border border-red-900/60 bg-red-950/30 px-3.5 py-2.5"
                >
                  <FaExclamationCircle className="mt-0.5 shrink-0 text-sm text-red-400" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs leading-relaxed text-red-200/90">{error.message}</p>
                    {error.code !== 'unconfigured' && (
                      <button
                        type="button"
                        onClick={retry}
                        className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-red-900/70 px-2.5 py-1 text-[11px] text-red-200 transition-colors hover:bg-red-950/60"
                      >
                        <FaRedo className="text-[9px]" /> Try again
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Announce replies to screen readers without duplicating them visually. */}
              <p className="sr-only" aria-live="polite">
                {status === 'waiting' ? 'Riv is thinking' : status === 'idle' ? 'Reply ready' : ''}
              </p>
            </div>

            {/* Composer */}
            <div className="border-t border-gray-800 bg-off-black/60 px-3 py-3">
              <div className="flex items-end gap-2 rounded-xl border border-gray-800 bg-black px-3 py-2 transition-colors focus-within:border-moss-deep">
                <textarea
                  ref={inputRef}
                  rows={1}
                  value={draft}
                  onChange={onDraftChange}
                  onKeyDown={onKeyDown}
                  maxLength={maxLength}
                  placeholder="Ask about a project, a price, a timeline…"
                  aria-label="Your message"
                  /* 16px on phones is not a style choice: iOS Safari zooms the whole
                     page when a focused input is smaller, and it does not zoom back. */
                  className="max-h-[120px] flex-1 resize-none bg-transparent text-base leading-relaxed text-silver-light placeholder:text-silver-dark focus:outline-none sm:text-sm"
                />
                {busy ? (
                  <button
                    type="button"
                    onClick={stop}
                    aria-label="Stop generating"
                    className="grid h-8 w-8 shrink-0 place-items-center rounded-lg border border-gray-800 text-silver-medium transition-colors hover:border-moss-deep hover:text-moss-light"
                  >
                    <FaStop className="text-[11px]" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => submit()}
                    disabled={!draft.trim()}
                    aria-label="Send message"
                    className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-moss text-black transition-all hover:bg-moss-light disabled:cursor-not-allowed disabled:bg-gray-800 disabled:text-silver-dark"
                  >
                    <FaArrowUp className="text-xs" />
                  </button>
                )}
              </div>
              <div className="mt-1.5 flex items-center justify-between px-1">
                <p className="text-[10px] text-silver-dark">
                  AI assistant — for anything binding, we confirm in writing.
                </p>
                {nearLimit && (
                  <p className="text-[10px] text-silver-dark">
                    {draft.length}/{maxLength}
                  </p>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatWidget;
